import React, { useContext, useEffect, useState } from "react";
import Backdrop from "@material-ui/core/Backdrop";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import ColorModeContext from "../../layout/themeContext";
import { resolveAppName } from "../../config";
import useLoadingProgress from "../../hooks/useLoadingProgress";

const useStyles = makeStyles((theme) => {
  const isLight = theme.mode === "light";
  const accent = theme.palette.primary.main;

  return {
    backdrop: {
      zIndex: theme.zIndex.modal + 2,
      color: accent,
      backgroundColor: isLight
        ? "rgba(231, 237, 243, 0.92)"
        : "rgba(17, 24, 39, 0.94)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    panel: {
      width: "100%",
      maxWidth: 300,
      padding: theme.spacing(3.5, 3),
      borderRadius: 16,
      textAlign: "center",
      backgroundColor: isLight ? "#ffffff" : "#1f2937",
      border: `1px solid ${isLight ? "rgba(148, 163, 184, 0.28)" : "rgba(148, 163, 184, 0.16)"}`,
      boxShadow: isLight
        ? "0 16px 40px rgba(15, 23, 42, 0.08)"
        : "0 16px 40px rgba(0, 0, 0, 0.35)",
      animation: "$fadeIn 0.35s ease-out",
    },
    "@keyframes fadeIn": {
      from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
      to: { opacity: 1, transform: "translateY(0) scale(1)" },
    },
    spinnerWrap: {
      position: "relative",
      width: 56,
      height: 56,
      margin: "0 auto",
      marginBottom: theme.spacing(2),
    },
    ring: {
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      border: `3px solid ${isLight ? "rgba(148, 163, 184, 0.22)" : "rgba(148, 163, 184, 0.18)"}`,
      borderTopColor: accent,
      animation: "$spin 0.9s linear infinite",
    },
    ringInner: {
      position: "absolute",
      inset: 10,
      borderRadius: "50%",
      border: "2px solid transparent",
      borderBottomColor: isLight ? "rgba(6, 81, 131, 0.35)" : "rgba(96, 165, 250, 0.45)",
      animation: "$spinReverse 1.2s linear infinite",
    },
    "@keyframes spin": {
      to: { transform: "rotate(360deg)" },
    },
    "@keyframes spinReverse": {
      to: { transform: "rotate(-360deg)" },
    },
    brand: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: isLight ? "#64748b" : "#94a3b8",
      marginBottom: theme.spacing(1.25),
    },
    title: {
      fontSize: 15,
      fontWeight: 600,
      color: isLight ? "#0f172a" : "#f8fafc",
      marginBottom: theme.spacing(1),
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      marginBottom: theme.spacing(1.5),
      minHeight: 20,
    },
    subtitle: {
      fontSize: 13,
      color: isLight ? "#64748b" : "#94a3b8",
      textAlign: "left",
    },
    percent: {
      fontSize: 12,
      fontWeight: 700,
      color: accent,
      minWidth: 38,
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
    progressTrack: {
      width: "100%",
      height: 4,
      borderRadius: 999,
      backgroundColor: isLight ? "rgba(148, 163, 184, 0.22)" : "rgba(148, 163, 184, 0.18)",
      overflow: "hidden",
      marginBottom: theme.spacing(1.5),
    },
    progressFill: {
      height: "100%",
      borderRadius: 999,
      backgroundColor: accent,
      transition: "width 0.35s ease",
    },
    dots: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      minHeight: 8,
    },
    dot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: accent,
      animation: "$pulse 1.2s ease-in-out infinite",
    },
    dot2: {
      animationDelay: "0.15s",
    },
    dot3: {
      animationDelay: "0.3s",
    },
    "@keyframes pulse": {
      "0%, 80%, 100%": { opacity: 0.25, transform: "scale(0.85)" },
      "40%": { opacity: 1, transform: "scale(1)" },
    },
  };
});

const loadingMessages = [
  "loading.preparing",
  "loading.connecting",
  "loading.syncing",
];

const BackdropLoading = () => {
  const classes = useStyles();
  const { appName } = useContext(ColorModeContext);
  const [messageIndex, setMessageIndex] = useState(0);
  const { progress } = useLoadingProgress(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  const statusKey = loadingMessages[messageIndex];
  const statusText = i18n.exists(statusKey)
    ? i18n.t(statusKey)
    : i18n.t("loading.pleaseWait");

  return (
    <Backdrop className={classes.backdrop} open>
      <div className={classes.panel}>
        <Typography className={classes.brand}>
          {resolveAppName(appName)}
        </Typography>

        <div className={classes.spinnerWrap}>
          <div className={classes.ring} />
          <div className={classes.ringInner} />
        </div>

        <Typography className={classes.title}>
          {i18n.t("loading.loadingSystem")}
        </Typography>

        <div className={classes.statusRow}>
          <Typography className={classes.subtitle}>{statusText}</Typography>
          <Typography className={classes.percent}>{progress}%</Typography>
        </div>

        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={classes.dots}>
          <span className={classes.dot} />
          <span className={`${classes.dot} ${classes.dot2}`} />
          <span className={`${classes.dot} ${classes.dot3}`} />
        </div>
      </div>
    </Backdrop>
  );
};

export default BackdropLoading;
